import React from "react";
import { Link } from "react-router-dom";
import { mdiAlertOutline, mdiVectorLink } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";

import NOAStatus from "components/NOAStatus";
import {
  InvoicingSVGIcon,
  InvoicingOffPlatformSVGIcon,
  NoInvoicingSVGIcon
} from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import { routes } from "constants/routes";
import { formatNumber, numberFormatter } from "helpers/formatNumber";
import {
  FUNDING_REQUEST_CONTRACT_TYPES_MAP,
  FundingRequestContractType
} from "helpers/constantTypes";
import { ITableHeaderList } from "models/HaulPay";
import StatusCell from "./StatusCell";

interface StatusDotProps {
  status?: string;
  tooltipPosition?: "top" | "bottom";
}

const StatusDot: React.FC<StatusDotProps> = ({ status, tooltipPosition = "bottom" }) => {
  let bgColor = "";
  let tooltip = "";

  switch (status?.toLowerCase()) {
    case "approved":
      bgColor = "bg-green-500";
      tooltip = "Approved";
      break;
    case "pending":
      bgColor = "bg-orange-500";
      tooltip = "Pending";
      break;
    case "needs_review":
    case "needs review":
      bgColor = "bg-yellow-500";
      tooltip = "Needs review";
      break;
    case "declined":
      bgColor = "bg-red-500";
      tooltip = "Declined";
      break;
    default:
      bgColor = "bg-gray-400";
      tooltip = "Unknown status";
  }

  const tooltipPosition1 = tooltipPosition === "top" ? "bottom-full mb-2" : "top-full mt-2";

  return (
    <div className="flex items-center justify-center relative group">
      <div className={`w-2 h-2 rounded-full ${bgColor}`}></div>
      <div
        className={`absolute ${tooltipPosition1} left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10`}>
        {tooltip}
      </div>
    </div>
  );
};

const InvoiceNumberRenderer: React.FC<{ item: any }> = ({ item }) => (
  <div className="flex items-center gap-2">
    <p className="text-md color-primary">{item?.invoiceNumber}</p>
    <Link
      to={`${routes.HAULPAY_BROKER_FUNDING_REQUEST}/${item?.id}`}
      className="text-primary hover:text-primary-dark">
      <Icon path={mdiVectorLink} size={0.8} />
    </Link>
  </div>
);

const DebtorRenderer: React.FC<{ item: any }> = ({ item }) => {
  if (!item) {
    return <div />;
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-text">{item}</span>
    </div>
  );
};

const NOAStatusRenderer: React.FC<{ items: any }> = ({ items }) => (
  <NOAStatus
    {...items?.factoringDebtorRelation}
    debtorId={items?.id}
    factoringId={items?.factoringId}
  />
);

export const CARRIER_NOA_STATUS_HEADER = {
  value: ["factoringDebtorRelation", "debtor", "factoring"],
  label: "NOA Status",
  dbSortName: "noa_status",
  render: (items: any) =>
    items?.debtor?.id ? (
      <NOAStatus
        noaActive={Boolean(items?.factoringDebtorRelation?.noaActive)}
        noaSent={Boolean(items?.factoringDebtorRelation?.noaSent)}
        noaRequested={Boolean(items?.factoringDebtorRelation?.noaRequested)}
        creditApproved={items?.factoringDebtorRelation?.creditApproved}
        debtorId={items?.debtor?.id}
        factoringId={items?.factoring?.id}
      />
    ) : (
      <span>-</span>
    )
};

const InvoiceAmountRenderer: React.FC<{ item: any }> = ({ item }) => (
  <div className="flex items-center gap-0.5">
    <span>{formatNumber(item?.amount, 2)}</span>
    {item?.contractType === "SELF_FINANCE_NO_INVOICE" || item?.name === "No Debtor" ? (
      <Tooltip message="Self-Finance No Invoice">
        <NoInvoicingSVGIcon className="size-5 text-approvedBorder" />
      </Tooltip>
    ) : item?.contractType === "SELF_FINANCE_WITH_INVOICE" ? (
      <Tooltip message="Self-Finance With Invoice">
        <InvoicingSVGIcon className="size-5 text-approvedBorder" />
      </Tooltip>
    ) : item?.contractType === "SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM" ? (
      <Tooltip message="Self-Finance With Invoice (Off-Platform)">
        <InvoicingOffPlatformSVGIcon className="text-approvedBorder" />
      </Tooltip>
    ) : null}
  </div>
);

const InvoiceSentRenderer: React.FC<{ items: string[] }> = ({ items }) =>
  Array.isArray(items) && items.length > 0 ? (
    <Tooltip
      position="down"
      message={
        <div className="flex flex-col gap-2">
          <span>Invoice Sent On:</span>
          {items.map((item: string, key: number) => (
            <span className="text-sm text-text" key={key}>
              {dayjs(item).format("MM/DD/YYYY hh:mm:ss A")}
            </span>
          ))}
        </div>
      }
      wrapperClass="text-approvedBorder">
      <span className="text-approvedBorder text-sm">YES</span>
    </Tooltip>
  ) : (
    <span className="text-error text-sm">NO</span>
  );

const CarrierRenderer: React.FC<{ items: any }> = ({ items }) => {
  if (!items.companyName) {
    return <div />;
  }
  const status = items?.status || "unknown";
  return (
    <div className="flex items-center gap-2">
      <StatusDot status={status} />
      <span className="text-sm text-text">{items.companyName}</span>
    </div>
  );
};

const CarrierRateRenderer: React.FC<{ item: number }> = ({ item }) => (
  <span className="text-sm text-text">{numberFormatter(item)}%</span>
);

const CarrierPayRenderer: React.FC<{ item: number }> = ({ item }) => (
  <span className="text-sm text-text">{formatNumber(item, 2)}</span>
);

const ContractTypeRenderer: React.FC<{
  contractType: FundingRequestContractType;
}> = ({ contractType }) => (
  <div>
    <p className="capitalize">{FUNDING_REQUEST_CONTRACT_TYPES_MAP[contractType]}</p>
  </div>
);

const CreatedAtRenderer: React.FC<{ item: string }> = ({ item }) => (
  <span className="text-sm text-text">{dayjs(item).format("MM/DD/YYYY hh:mm A")}</span>
);

const StatusRenderer: React.FC<{ item: any }> = ({ item }) => <StatusCell item={item} />;

const WarningRenderer: React.FC = () => (
  <Tooltip message="You can specify the text here for different kinds of error.">
    <Icon path={mdiAlertOutline} className="text-error" />
  </Tooltip>
);

const AmountFundedRenderer: React.FC<{ item: number }> = ({ item }) => (
  <span className="text-sm text-text">{formatNumber(item, 2)}</span>
);

const DetailsRenderer: React.FC<{ item: string }> = ({ item }) => (
  <Link
    to={`${routes.HAULPAY_BROKER_FUNDING_REQUEST}/${item}`}
    className="px-4 py-3 border border-primary rounded-base text-primary bg-background">
    More
  </Link>
);

export const MANAGE_PAY_TABLE_HEADER_LIST: ITableHeaderList[] = [
  {
    value: ["invoiceNumber", "id"],
    label: "Invoice #",
    dbSortName: "invoice_number",
    render: (item: any) => <InvoiceNumberRenderer item={item} />
  },
  {
    value: "userLoadNumber",
    label: "Load #",
    dbSortName: "load_number",
    rowStyle: "!w-40 !break-all"
  },
  {
    value: "debtor.name",
    label: "Debtor",
    dbSortName: "debtor",
    rowStyle: "!w-44 !break-words",
    render: (item: any) => <DebtorRenderer item={item} />
  },
  {
    value: ["noaStatus", "factoringDebtorRelation", "factoringId", "debtor.id"],
    label: "NOA Status",
    dbSortName: "noa_status",
    render: (items: any) => <NOAStatusRenderer items={items} />
  },
  {
    value: ["amount", "contractType", "debtor.name"],
    label: "Invoice Amnt",
    dbSortName: "invoice_amount",
    render: (item: any) => <InvoiceAmountRenderer item={item} />
  },
  {
    value: "lastInvoiceSentTime",
    label: "Invoice Sent",
    dbSortName: "invoice_sent",
    render: (items: string[]) => <InvoiceSentRenderer items={items} />
  },
  {
    value: ["paymentProfile.companyName", "paymentProfile.status"],
    label: "Carrier",
    dbSortName: "carrier",
    render: (items: any) => <CarrierRenderer items={items} />
  },
  {
    value: "paymentProfileDiscountRate",
    label: "Carrier Rate",
    dbSortName: "carrier_rate",
    render: (item: number) => <CarrierRateRenderer item={item} />
  },
  {
    value: "paymentProfileAmount",
    label: "Carrier Pay",
    dbSortName: "carrier_pay",
    render: (item: number) => <CarrierPayRenderer item={item} />
  },
  {
    value: "contractType",
    label: "Type",
    dbSortName: "type",
    render: (contractType: FundingRequestContractType) => (
      <ContractTypeRenderer contractType={contractType} />
    )
  },
  {
    value: "created",
    label: "Created At",
    dbSortName: "created",
    render: (item: string) => <CreatedAtRenderer item={item} />
  },
  {
    value: ["status", "id", "attachments", "factoring", "contractType", "statusAt"],
    label: "Status",
    dbSortName: "status_at",
    rowStyle: "!min-w-[140px]",
    render: (item: any) => <StatusRenderer item={item} />
  },
  {
    value: "warning",
    label: " ",
    isShow: false,
    render: () => <WarningRenderer />
  },
  {
    value: "amountFunded",
    label: "Amnt Funded",
    dbSortName: "amount_funded",
    render: (item: number) => <AmountFundedRenderer item={item} />
  },
  {
    value: "id",
    label: "Details",
    render: (item: string) => <DetailsRenderer item={item} />
  }
];
